import { BoxOfferRulePeriod, OfferAutonomousPurchase } from "@/modules/boxOffer/entities/boxOfferTypes"
import { PriceObjectDtoSchema } from "@/modules/common/priceObject/gateway/priceObjectApiDto"
import { CoachOfferType, CoachPaymentOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { z } from "zod"

export const CoachOfferPriceDtoSchema = z
  .object({
    id: z.string(),
    price: PriceObjectDtoSchema.nullable(),
    recurringPeriod: z.string().nullable(),
    type: z.enum(CoachPaymentOfferType),
    title: z.string(),
    count: z.number().nullable(),
    minimumCommitmentPeriod: z.string().nullable(),
    commitmentPeriodAutoRenewal: z.boolean().nullable(),
    validityPeriodMonths: z.number().nullable(),
    renewable: z.boolean().nullable(),
    creationDate: z.string(),
  })
  .meta({ id: "CoachOfferPriceDtoSchema" })

export const CoachOfferRuleDtoSchema = z
  .object({
    value: z.number(),
    period: z.enum(BoxOfferRulePeriod),
  })
  .meta({ id: "CoachOfferRuleDtoSchema" })

export const CoachWaiverDetailsDtoSchema = z
  .object({
    url: z.string(),
    fileName: z.string(),
    acceptedAt: z.string().nullable(),
  })
  .meta({ id: "CoachWaiverDetailsDtoSchema" })

export const CoachOfferDetailsDtoSchema = z
  .object({
    id: z.string(),
    type: z.enum(CoachOfferType),
    name: z.string(),
    image: HUImageDtoSchema.nullable(),
    description: z.string().nullable(),
    renewable: z.boolean().nullable(),
    showcaseDisplay: z.boolean(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    archived: z.boolean(),
    waiverDetails: CoachWaiverDetailsDtoSchema.nullable(),
    displayOrder: z.number().nullable(),
    paymentOffers: z.array(CoachOfferPriceDtoSchema),
    programs: z.array(z.string()),
    rules: z.array(CoachOfferRuleDtoSchema),
  })
  .meta({ id: "CoachOfferDetailsDtoSchema" })

export const PaymentOfferCreationInputSchema = z
  .object({
    price: PriceObjectDtoSchema.nullable(),
    recurringPeriod: z.string().nullable(),
    type: z.enum(CoachPaymentOfferType),
    title: z.string(),
    count: z.number().int().nullable(),
    minimumCommitmentPeriod: z.string().nullable(),
    commitmentPeriodAutoRenewal: z.boolean().nullable(),
    validityPeriodMonths: z.number().int().nullable(),
    displayOrder: z.number().int().nullable(),
    renewable: z.boolean().nullable(),
  })
  .meta({ id: "PaymentOfferCreationInputSchema" })

export const CoachOfferCreationInputSchema = z
  .object({
    name: z.string(),
    type: z.enum(CoachOfferType),
    description: z.string().nullable(),
    showcaseDisplay: z.boolean().nullable(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    displayOrder: z.number().int().nullable(),
    paymentOffers: z.array(PaymentOfferCreationInputSchema),
    imageKey: z.string().nullable(),
    rules: z.array(CoachOfferRuleDtoSchema).nullable(),
    programRestrictions: z.array(z.string()),
    defaultHideSessionContent: z.boolean().nullable(),
    scoreboardVisibility: z.boolean().nullable(),
  })
  .meta({ id: "CoachOfferCreationInputSchema" })

export const CoachOfferUpdateInputSchema = z
  .object({
    name: z.string(),
    description: z.string().nullable(),
    showcaseDisplay: z.boolean().nullable(),
    priceDisplay: z.boolean(),
    autonomousPurchase: z.enum(OfferAutonomousPurchase),
    displayOrder: z.number().int().nullable(),
    imageKey: z.string().nullable(),
    rules: z.array(CoachOfferRuleDtoSchema).nullable(),
    programRestrictions: z.array(z.string()),
    defaultHideSessionContent: z.boolean().nullable(),
    scoreboardVisibility: z.boolean().nullable(),
  })
  .meta({ id: "CoachOfferUpdateInputSchema" })

export type CoachOfferDetailsDto = z.infer<typeof CoachOfferDetailsDtoSchema>
export type CoachOfferPriceDto = z.infer<typeof CoachOfferPriceDtoSchema>
export type CoachOfferRuleDto = z.infer<typeof CoachOfferRuleDtoSchema>
export type PaymentOfferCreationInput = z.infer<typeof PaymentOfferCreationInputSchema>
export type CoachOfferCreationInput = z.infer<typeof CoachOfferCreationInputSchema>
export type CoachOfferUpdateInput = z.infer<typeof CoachOfferUpdateInputSchema>
